<template>
  <v-container fluid class="kids-golf-registration pa-0">
    <section class="hero-section">
      <div class="hero-content">
        <v-avatar color="white" size="56" class="hero-mark elevation-3">
          <v-icon color="primary" size="30">mdi-golf</v-icon>
        </v-avatar>
        <div>
          <span class="hero-eyebrow">Club Campestre de San Luis Potosi</span>
          <h1>Registro al torneo</h1>
          <p>Torneo infantil - juvenil, primera etapa</p>
        </div>
      </div>
    </section>

    <v-row justify="center" class="page-content ma-0">
      <v-col cols="12" sm="10" md="8" lg="7" xl="6" class="px-4 px-sm-0">
        <v-alert v-if="error" type="error" text dismissible class="mb-5 error-alert" @input="error = null">
          {{ error }}
        </v-alert>

        <v-card v-if="completed && registrationData" class="success-card elevation-4">
          <v-card-text class="pa-6 pa-sm-8">
            <div class="text-center mb-6">
              <v-avatar color="success" size="68" class="mb-4">
                <v-icon color="white" size="40">mdi-check</v-icon>
              </v-avatar>
              <h2 class="headline font-weight-bold primary--text mb-2">¡Registro completado!</h2>
              <p class="body-1 grey--text">
                {{ registrationData.parent?.full_name }}, tu grupo ha sido registrado exitosamente.
              </p>
            </div>

            <v-divider class="my-6"></v-divider>

            <!-- Access Code -->
            <div class="mb-6 pa-5 text-center" style="background: #e3f2fd; border-radius: 8px; border-left: 4px solid #2196f3">
              <div class="text-caption font-weight-bold grey--text mb-3 text-uppercase">Código de acceso para check-in</div>
              <v-chip x-large color="primary" text-color="white" class="font-weight-bold px-6 py-7" style="font-size: 1.2rem">
                {{ registrationData.parent?.access_code }}
              </v-chip>
              <p class="text-caption grey--text mt-4 mb-0">
                <v-icon small>mdi-information-outline</v-icon>
                Se ha enviado un mensaje de confirmación por WhatsApp con el código QR y ubicación de la caseta.
              </p>
            </div>

            <v-divider class="my-6"></v-divider>

            <!-- Summary -->
            <div class="summary-box text-left pa-5 mb-6">
              <div class="summary-row"><span>Tutor</span><strong>{{ registrationData.parent?.full_name }}</strong></div>
              <div class="summary-row"><span>Jugadores</span><strong>{{ registrationData.children?.length || 0 }}</strong></div>
              <div class="summary-row"><span>Acompañantes</span><strong>{{ registrationData.guests?.length || 0 }}</strong></div>
            </div>

            <!-- Children List -->
            <div v-if="registrationData.children && registrationData.children.length > 0" class="mb-6">
              <h3 class="subtitle-2 font-weight-bold primary--text mb-3">Jugadores Registrados</h3>
              <div v-for="child in registrationData.children" :key="child.id" class="child-summary pa-3 mb-2">
                <div class="d-flex align-center">
                  <v-avatar :color="getGenderColor(child.gender)" size="36" class="mr-3">
                    <v-icon small color="white">{{ getGenderIcon(child.gender) }}</v-icon>
                  </v-avatar>
                  <div class="flex-grow-1">
                    <div class="font-weight-medium">{{ child.full_name }}</div>
                    <div class="text-caption grey--text" v-if="child.age">{{ child.age }} años</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Guests List -->
            <div v-if="registrationData.guests && registrationData.guests.length > 0" class="mb-6">
              <h3 class="subtitle-2 font-weight-bold primary--text mb-3">Acompañantes Registrados</h3>
              <div v-for="guest in registrationData.guests" :key="guest.id" class="guest-summary pa-3 mb-2">
                <div class="d-flex align-center">
                  <v-avatar color="secondary" size="36" class="mr-3">
                    <v-icon small color="white">mdi-account-heart</v-icon>
                  </v-avatar>
                  <div class="flex-grow-1">
                    <div class="font-weight-medium">{{ guest.full_name }}</div>
                    <div class="text-caption grey--text">{{ guest.relationship }}</div>
                  </div>
                </div>
              </div>
            </div>

            <v-btn color="primary" block depressed large class="register-button" @click="resetRegistration">
              <v-icon left>mdi-plus</v-icon>
              Registrar otro grupo
            </v-btn>
          </v-card-text>
        </v-card>

        <v-card v-else class="registration-card elevation-4">
          <v-stepper v-model="step" class="registration-stepper">
            <v-stepper-header class="stepper-header elevation-0">
              <v-stepper-step :complete="step > 1" step="1">Tutor</v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="step > 2" step="2">Jugador</v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step step="3">Invitados</v-stepper-step>
            </v-stepper-header>

            <v-stepper-items>
              <v-stepper-content step="1" class="step-content pa-5 pa-sm-6">
                <step-heading icon="mdi-account-outline" title="Datos del padre o tutor" subtitle="Usaremos estos datos para comunicarnos contigo." />
                <v-form ref="parentForm" lazy-validation>
                  <div class="field-group">
                    <label class="field-label">Nombre completo <span>*</span></label>
                    <v-text-field v-model="parent.full_name" outlined hide-details="auto" placeholder="Ej. Juan Perez Lopez" prepend-icon="mdi-account" class="app-input" :rules="nameRules"></v-text-field>
                  </div>
                  <div class="field-group">
                    <label class="field-label">Telefono <span>*</span></label>
                    <v-row dense>
                      <v-col cols="5">
                        <v-select v-model="phoneLada" :items="ladaOptions" outlined hide-details="auto" prepend-icon="mdi-phone" class="app-input" :rules="requiredRules"></v-select>
                      </v-col>
                      <v-col cols="7">
                        <v-text-field
                          v-model="phoneNumber"
                          outlined
                          hide-details="auto"
                          placeholder="Ej. 1234567890"
                          type="tel"
                          class="app-input"
                          @input="phoneNumber = phoneNumber.replace(/\D/g, '').slice(0, 10)"
                          :rules="[
                            v => !!v || 'Número es requerido',
                            v => (v && v.length >= 10) || 'Mínimo 10 dígitos'
                          ]"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <small class="field-hint">Te enviaremos la confirmacion por WhatsApp. Formato: LADA + número (10 dígitos)</small>
                  </div>
                  <div class="field-group">
                    <label class="field-label">Correo electronico <em>Opcional</em></label>
                    <v-text-field v-model="parent.email" outlined hide-details="auto" placeholder="Ej. juan@correo.com" type="email" prepend-icon="mdi-email" class="app-input" :rules="emailRules"></v-text-field>
                  </div>
                  <div class="field-group mb-7">
                    <label class="field-label">Club <span>*</span></label>
                    <v-text-field v-model="parent.club" outlined hide-details="auto" placeholder="Ej. Club Campestre" prepend-icon="mdi-golf" class="app-input" :rules="requiredRules"></v-text-field>
                  </div>
                  <v-btn color="primary" block depressed large class="register-button" @click="goToChild">
                    Continuar
                    <v-icon right>mdi-arrow-right</v-icon>
                  </v-btn>
                </v-form>
              </v-stepper-content>

              <v-stepper-content step="2" class="step-content pa-5 pa-sm-6">
                <step-heading icon="mdi-account-child-outline" title="Datos de los jugadores" subtitle="Puedes registrar hasta tres participantes infantiles o juveniles." />
                <v-form ref="childForm" lazy-validation>
                  <div v-for="(child, index) in children" :key="index" class="child-form pa-4 mb-4">
                    <div class="d-flex align-center mb-4">
                      <span class="subtitle-2 font-weight-bold primary--text">Jugador {{ index + 1 }}</span>
                      <v-spacer></v-spacer>
                      <v-btn v-if="children.length > 1" icon small color="error" aria-label="Eliminar jugador" @click="removeChild(index)">
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </div>
                    <div class="field-group">
                      <label class="field-label">Nombre completo <span>*</span></label>
                      <v-text-field v-model="child.full_name" outlined hide-details="auto" placeholder="Ej. Sofia Martinez" prepend-icon="mdi-account-child" class="app-input" :rules="nameRules"></v-text-field>
                    </div>
                    <v-row dense>
                      <v-col cols="5">
                        <div class="field-group mb-0">
                          <label class="field-label">Edad <span>*</span></label>
                          <v-text-field v-model.number="child.age" outlined hide-details="auto" type="number" min="1" max="18" prepend-icon="mdi-cake-variant" class="app-input" :rules="ageRules"></v-text-field>
                        </div>
                      </v-col>
                      <v-col cols="7">
                        <div class="field-group mb-0">
                          <label class="field-label">Genero <span>*</span></label>
                          <v-select v-model="child.gender" :items="genderOptions" outlined hide-details="auto" prepend-icon="mdi-account-group-outline" class="app-input" :rules="requiredRules"></v-select>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                  <v-btn v-if="children.length < 3" block outlined color="secondary" class="add-child mb-6" @click="addChild">
                    <v-icon left>mdi-plus</v-icon>
                    Agregar jugador
                  </v-btn>
                  <v-alert v-else text dense type="info" class="mb-6">Has llegado al maximo de jugadores.</v-alert>
                  <div class="step-actions">
                    <v-btn text color="primary" @click="step = 1"><v-icon left>mdi-arrow-left</v-icon>Atras</v-btn>
                    <v-btn color="primary" depressed large class="register-button action-next" @click="goToGuests">Continuar<v-icon right>mdi-arrow-right</v-icon></v-btn>
                  </div>
                </v-form>
              </v-stepper-content>

              <v-stepper-content step="3" class="step-content pa-5 pa-sm-6">
                <step-heading icon="mdi-account-multiple-outline" title="Acompanantes" subtitle="Puedes agregar hasta tres invitados. Este paso es opcional." />
                <div v-for="(guest, index) in guests" :key="index" class="guest-form pa-4 mb-4">
                  <div class="d-flex align-center mb-4">
                    <span class="subtitle-2 font-weight-bold primary--text">Acompañante {{ index + 1 }}</span>
                    <v-spacer></v-spacer>
                    <v-btn icon small color="error" aria-label="Eliminar acompanante" @click="removeGuest(index)">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </div>
                  <div class="field-group mb-4">
                    <label class="field-label">Nombre completo <span>*</span></label>
                    <v-text-field v-model="guest.full_name" outlined hide-details="auto" placeholder="Ej. Maria Garcia" prepend-icon="mdi-account" class="app-input"></v-text-field>
                  </div>
                  <div class="field-group mb-0">
                    <label class="field-label">Relacion <span>*</span></label>
                    <v-text-field v-model="guest.relationship" outlined hide-details="auto" placeholder="Ej. Abuela, hermano, tía" prepend-icon="mdi-account-heart-outline" class="app-input"></v-text-field>
                  </div>
                </div>
                <v-btn v-if="guests.length < 3" block outlined color="secondary" class="add-guest mb-6" @click="addGuest">
                  <v-icon left>mdi-plus</v-icon>
                  Agregar acompañante
                </v-btn>
                <v-alert v-else text dense type="info" class="mb-6">Has llegado al máximo de acompañantes.</v-alert>
                <div class="step-actions">
                  <v-btn text color="primary" @click="step = 2"><v-icon left>mdi-arrow-left</v-icon>Atras</v-btn>
                  <v-btn color="primary" depressed large class="register-button action-next" :loading="isSubmitting" @click="completeRegistration">
                    Finalizar registro
                    <v-icon right>mdi-check</v-icon>
                  </v-btn>
                </div>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { kidsGolfTournamentAPI } from '@/services/kidsGolfTournament'

export default {
  name: 'KidsGolfPreRegister',
  components: {
    StepHeading: {
      props: ['icon', 'title', 'subtitle'],
      template: `
        <div class="step-heading mb-6">
          <v-avatar color="secondary" size="42" class="mr-3"><v-icon color="white">{{ icon }}</v-icon></v-avatar>
          <div><div class="subtitle-1 font-weight-bold primary--text">{{ title }}</div><div class="caption grey--text">{{ subtitle }}</div></div>
        </div>
      `
    }
  },
  data () {
    return {
      step: 1,
      completed: false,
      isSubmitting: false,
      error: null,
      inviterToken: null,
      registrationData: null,
      parent: { full_name: '', phone: '', email: '', club: '' },
      phoneLada: '52',
      phoneNumber: '',
      children: [{ full_name: '', age: null, gender: '' }],
      guests: [],
      ladaOptions: [
        { text: '+52 México', value: '52' },
        { text: '+1 USA / Canadá', value: '1' },
        { text: '+55 Brasil', value: '55' },
        { text: '+57 Colombia', value: '57' },
        { text: '+51 Perú', value: '51' },
        { text: '+56 Chile', value: '56' },
        { text: '+54 Argentina', value: '54' },
        { text: '+58 Venezuela', value: '58' },
        { text: '+34 España', value: '34' },
        { text: '+39 Italia', value: '39' },
        { text: '+33 Francia', value: '33' },
        { text: '+49 Alemania', value: '49' },
        { text: '+44 Reino Unido', value: '44' },
        { text: '+31 Países Bajos', value: '31' },
        { text: '+41 Suiza', value: '41' },
        { text: '+43 Austria', value: '43' },
        { text: '+32 Bélgica', value: '32' },
        { text: '+45 Dinamarca', value: '45' },
        { text: '+46 Suecia', value: '46' },
        { text: '+47 Noruega', value: '47' },
        { text: '+61 Australia', value: '61' },
        { text: '+64 Nueva Zelanda', value: '64' },
        { text: '+81 Japón', value: '81' },
        { text: '+86 China', value: '86' },
        { text: '+91 India', value: '91' },
        { text: '+82 Corea del Sur', value: '82' },
        { text: '+66 Tailandia', value: '66' },
        { text: '+60 Malasia', value: '60' },
        { text: '+65 Singapur', value: '65' },
        { text: '+63 Filipinas', value: '63' },
        { text: '+27 Sudáfrica', value: '27' },
        { text: '+212 Marruecos', value: '212' },
        { text: '+20 Egipto', value: '20' },
        { text: '+Otro país', value: 'other' }
      ],
      genderOptions: [
        { text: 'Masculino', value: 'M' },
        { text: 'Femenino', value: 'F' },
        { text: 'Otro', value: 'O' }
      ],
      requiredRules: [value => !!value || 'Este campo es requerido'],
      nameRules: [
        value => !!value || 'El nombre es requerido',
        value => (value && value.length <= 255) || 'Maximo 255 caracteres'
      ],
      phoneRules: [
        value => !!value || 'El telefono es requerido',
        value => (value && value.length <= 20) || 'Maximo 20 caracteres'
      ],
      ageRules: [
        value => (value !== null && value !== '') || 'La edad es requerida',
        value => (value >= 1 && value <= 18) || 'La edad debe estar entre 1 y 18'
      ],
      emailRules: [value => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || 'Correo invalido']
    }
  },
  methods: {
    goToChild () {
      if (!this.phoneNumber || this.phoneNumber.length < 10) {
        this.error = 'Por favor completa el número de teléfono (mínimo 10 dígitos)'
        return
      }
      if (this.$refs.parentForm.validate()) {
        this.step = 2
      }
    },
    goToGuests () {
      if (this.$refs.childForm.validate()) this.step = 3
    },
    addChild () {
      if (this.children.length < 3) this.children.push({ full_name: '', age: null, gender: '' })
    },
    removeChild (index) {
      this.children.splice(index, 1)
    },
    addGuest () {
      if (this.guests.length < 3) this.guests.push({ full_name: '', relationship: '' })
    },
    removeGuest (index) {
      this.guests.splice(index, 1)
    },
    async completeRegistration () {
      const hasInvalidGuest = this.guests.some(guest => !guest.full_name.trim() || !guest.relationship.trim())
      if (hasInvalidGuest) {
        this.error = 'Completa el nombre y relación de cada acompanante, o elimina el formulario vacío.'
        return
      }

      try {
        this.isSubmitting = true
        this.error = null

        const fullPhone = this.phoneLada === 'other' ? this.phoneNumber : `+${this.phoneLada}${this.phoneNumber}`

        const payload = {
          parent: {
            full_name: this.parent.full_name,
            phone: fullPhone
          },
          children: this.children.map(child => ({
            full_name: child.full_name,
            age: child.age ? parseInt(child.age) : null,
            gender: child.gender || null
          })),
          guests: this.guests.map(guest => ({
            full_name: guest.full_name,
            relationship: guest.relationship
          }))
        }

        if (this.parent.email) payload.parent.email = this.parent.email
        if (this.parent.club) payload.parent.club = this.parent.club

        const response = await kidsGolfTournamentAPI.completeRegistration(payload)

        if (response.data.success) {
          this.registrationData = response.data
          this.inviterToken = response.data.parent.inviter_token
          this.completed = true
        }
      } catch (err) {
        const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Error al completar el registro'
        this.error = errorMsg
      } finally {
        this.isSubmitting = false
      }
    },
    resetRegistration () {
      this.step = 1
      this.completed = false
      this.error = null
      this.inviterToken = null
      this.registrationData = null
      this.parent = { full_name: '', phone: '', email: '', club: '' }
      this.phoneLada = '52'
      this.phoneNumber = ''
      this.children = [{ full_name: '', age: null, gender: '' }]
      this.guests = []
    },
    getGenderColor (gender) {
      switch (gender) {
        case 'M':
          return 'blue'
        case 'F':
          return 'pink'
        case 'O':
          return 'purple'
        default:
          return 'grey'
      }
    },
    getGenderIcon (gender) {
      switch (gender) {
        case 'M':
          return 'mdi-human-male'
        case 'F':
          return 'mdi-human-female'
        case 'O':
          return 'mdi-human'
        default:
          return 'mdi-human'
      }
    }
  }
}
</script>

<style scoped>
.kids-golf-registration { min-height: 100vh; padding-bottom: 3rem; background: #f4f3f7; }
.hero-section { padding: 2rem 1.5rem 4.5rem; color: white; background: #2a2738; }
.hero-content { display: flex; align-items: center; width: 100%; max-width: 670px; margin: 0 auto; }
.hero-mark { flex: 0 0 auto; margin-right: 1rem; }
.hero-eyebrow { display: block; color: #d9d5ed; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
.hero-content h1 { margin: 0.25rem 0; font-size: 1.55rem; line-height: 1.2; }
.hero-content p { margin: 0; color: #e5e2ef; font-size: 0.9rem; }
.page-content { margin-top: -2.5rem !important; }
.registration-card, .success-card { overflow: hidden; border-radius: 8px !important; }
.registration-stepper { box-shadow: none; }
.stepper-header { min-height: 72px; padding: 0 0.25rem; }
::v-deep .v-stepper__label { font-size: 0.72rem; }
::v-deep .v-stepper__step { padding: 18px 6px; }
::v-deep .v-stepper__content { padding: 0 !important; }
.step-content { padding: 1.5rem 1.25rem !important; }
.step-heading { display: flex; align-items: center; }
.field-group { margin-bottom: 1.25rem; }
.field-label { display: block; margin-bottom: 0.45rem; color: #45424f; font-size: 0.82rem; font-weight: 700; }
.field-label span { color: #d64156; }
.field-label em { color: #777280; font-size: 0.75rem; font-style: normal; font-weight: 400; }
.field-hint { display: block; margin-top: 0.45rem; color: #777280; font-size: 0.75rem; }
::v-deep .app-input .v-input__slot { min-height: 52px !important; margin-bottom: 0; }
::v-deep .app-input .v-text-field__slot input { font-size: 1rem; }
.register-button { height: 52px !important; letter-spacing: 0 !important; }
.step-actions { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; margin-bottom: 0.75rem; }
.action-next { flex: 1; max-width: 210px; }
.child-form,
.guest-form { border: 1px solid #e4e2e9; border-radius: 8px; background: #faf9fc; }
.add-child,
.add-guest { height: 48px !important; border-style: dashed; letter-spacing: 0 !important; }
.error-alert { border-radius: 8px !important; }
.summary-box { border-radius: 8px; background: #f4f3f7; }
.summary-row { display: flex; justify-content: space-between; gap: 1rem; padding: 0.65rem 0; color: #686370; font-size: 0.9rem; }
.summary-row + .summary-row { border-top: 1px solid #e4e2e9; }
.summary-row strong { color: #2a2738; overflow-wrap: anywhere; text-align: right; }
@media (min-width: 600px) {
  .step-content { padding: 1.5rem !important; }
  .hero-section { padding-top: 2.75rem; }
  .hero-content h1 { font-size: 1.9rem; }
  .page-content { margin-top: -2.75rem !important; }
  .stepper-header { padding: 0 1rem; }
  ::v-deep .v-stepper__label { font-size: 0.85rem; }
  ::v-deep .v-stepper__step { padding: 24px 8px; }
}

.child-summary,
.guest-summary {
  border-radius: 8px;
  background: #f4f3f7;
  border: 1px solid #e4e2e9;
}

@media (min-width: 960px) {
  .step-content {
    padding-left: 3rem !important;
    padding-right: 3rem !important;
  }
}
</style>
